/** Stripe SetupIntent confirmation helpers for Payment Element flows. */

const STRIPE_REDIRECT_PARAMS = [
    'setup_intent',
    'setup_intent_client_secret',
    'redirect_status',
];

const RETURN_URL_QUERY_ALLOWLIST = ['tab'];
const PAYMENT_SESSION_RETURN_KEY = 'keyflo_payment_session_return';
const SETUP_REDIRECT_KEY = 'keyflo_stripe_setup_redirect';
const EXPECTED_SETUP_INTENT_KEY = 'keyflo_expected_setup_intent';

export const PAYMENT_SETUP_RETURN_PATH = '/payment/return';

export function setupIntentIdFromSecret(clientSecret) {
    if (!clientSecret) return null;
    return clientSecret.split('_secret_')[0] || null;
}

export function paymentSessionTokenFromPath(pathname) {
    const match = String(pathname || '').match(/^\/payment\/([^/]+)\/?$/);
    if (!match) return null;
    let token = match[1];
    try {
        token = decodeURIComponent(token);
    } catch {
        return null;
    }
    if (!token || token === 'return') return null;
    return token;
}

export function rememberExpectedSetupIntent(clientSecretOrId) {
    const raw = String(clientSecretOrId || '');
    const intentId = raw.includes('_secret_') ? setupIntentIdFromSecret(raw) : (raw || null);
    if (!intentId) return;
    sessionStorage.setItem(EXPECTED_SETUP_INTENT_KEY, intentId);
}

export function peekExpectedSetupIntent() {
    return sessionStorage.getItem(EXPECTED_SETUP_INTENT_KEY);
}

export function rememberPaymentSessionReturn(href = window.location.href) {
    const url = new URL(href, window.location.origin);
    const token = paymentSessionTokenFromPath(url.pathname);
    if (!token) return;
    sessionStorage.setItem(PAYMENT_SESSION_RETURN_KEY, token);
}

export function consumePaymentSessionReturn() {
    const token = sessionStorage.getItem(PAYMENT_SESSION_RETURN_KEY);
    if (token) sessionStorage.removeItem(PAYMENT_SESSION_RETURN_KEY);
    return token;
}

export function parseStripeSetupRedirect(search) {
    const params = new URLSearchParams(
        typeof search === 'string' || search == null
            ? search
            : search.toString(),
    );
    const setupIntentId = params.get('setup_intent');
    const clientSecret = params.get('setup_intent_client_secret');
    const redirectStatus = params.get('redirect_status');
    if (!setupIntentId && !clientSecret && !redirectStatus) return null;
    return {
        setupIntentId,
        clientSecret,
        redirectStatus,
        succeeded: redirectStatus === 'succeeded',
    };
}

function writeCapturedStripeSetupRedirect(redirect) {
    if (!redirect) return;
    sessionStorage.setItem(SETUP_REDIRECT_KEY, JSON.stringify(redirect));
}

export function peekStripeSetupRedirect(search = window.location.search) {
    const fromUrl = parseStripeSetupRedirect(search);
    if (fromUrl) {
        writeCapturedStripeSetupRedirect(fromUrl);
        return fromUrl;
    }
    try {
        const raw = sessionStorage.getItem(SETUP_REDIRECT_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

export function consumeStripeSetupRedirect(search = window.location.search) {
    const parsed = peekStripeSetupRedirect(search);
    sessionStorage.removeItem(SETUP_REDIRECT_KEY);
    return parsed;
}

/**
 * Use a Stripe redirect only when this tab created the SetupIntent.
 * Query-injected setup_intent ids from another account are dropped.
 */
export function consumeOwnedStripeSetupRedirect(search = window.location.search) {
    const parsed = peekStripeSetupRedirect(search);
    if (!parsed) return null;
    const parsedId = parsed.setupIntentId || setupIntentIdFromSecret(parsed.clientSecret);
    const expected = peekExpectedSetupIntent();
    sessionStorage.removeItem(SETUP_REDIRECT_KEY);
    if (!expected || !parsedId || parsedId !== expected) {
        return null;
    }
    sessionStorage.removeItem(EXPECTED_SETUP_INTENT_KEY);
    return parsed;
}

export function withoutStripeSetupRedirectParams(searchParams) {
    const next = new URLSearchParams(searchParams);
    STRIPE_REDIRECT_PARAMS.forEach((key) => next.delete(key));
    return next;
}

/**
 * Absolute page URL for SetupIntent confirmation.
 * Stripe requires return_url at confirm even when redirect is if_required
 * (3DS and other next-actions still need a landing path).
 *
 * Magic-link tokens and referral query params are not forwarded to Stripe.
 */
export function stripeSetupReturnUrl(href = window.location.href) {
    const url = new URL(href, window.location.origin);
    url.hash = '';
    const allowlisted = new URLSearchParams();
    RETURN_URL_QUERY_ALLOWLIST.forEach((key) => {
        const value = url.searchParams.get(key);
        if (value) allowlisted.set(key, value);
    });
    url.search = allowlisted.toString();
    if (paymentSessionTokenFromPath(url.pathname)) {
        url.pathname = PAYMENT_SETUP_RETURN_PATH;
    }
    return url.toString();
}

export async function confirmPaymentElementSetup(stripe, elements, { clientSecret } = {}) {
    rememberPaymentSessionReturn();
    rememberExpectedSetupIntent(clientSecret);
    const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
            return_url: stripeSetupReturnUrl(),
        },
        redirect: 'if_required',
    });
    const resolvedIntent = setupIntent || error?.setup_intent;
    const intentId = resolvedIntent?.id || setupIntentIdFromSecret(clientSecret);
    const alreadySucceeded = (
        resolvedIntent?.status === 'succeeded'
        || error?.code === 'setup_intent_unexpected_state'
    );
    return { error, setupIntent, resolvedIntent, intentId, alreadySucceeded };
}
